import React, { useEffect, useState } from 'react';
import { X, Key, Copy, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { generatePasswordResetLink, extractResetError } from '../../utils/passwordResetHelper';

interface TargetUser {
  uid: string;
  email: string;
  name?: string;
  lastname?: string;
}

interface PasswordResetLinkModalProps {
  user: TargetUser | null;
  onClose: () => void;
}

const PasswordResetLinkModal: React.FC<PasswordResetLinkModalProps> = ({ user, onClose }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    setLink(null);
    setError(null);
    setCopied(false);
    setLoading(true);

    generatePasswordResetLink(user.uid, currentUser)
      .then((generated) => {
        if (!cancelled) setLink(generated);
      })
      .catch((err) => {
        console.error('Error generating reset link:', err);
        if (!cancelled) setError(extractResetError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, currentUser]);

  const handleCopyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Error copiando:', e);
    }
  };

  if (!user) return null;

  const fullName = `${user.name || ''} ${user.lastname || ''}`.trim();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg max-w-lg w-full overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Reset password
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {fullName ? `${fullName} (${user.email})` : user.email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
              <span className="ml-3 text-sm text-gray-600">Generando link...</span>
            </div>
          )}

          {error && !loading && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-800">Error al generar el link</p>
              <p className="text-xs text-red-700 mt-1 break-words">{error}</p>
            </div>
          )}

          {link && !loading && (
            <>
              <p className="text-xs text-gray-600">
                Compartí este link <strong>solo</strong> con el usuario {user.email}.
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <code className="text-xs text-gray-800 break-all font-mono select-all block">
                  {link}
                </code>
              </div>
              <button
                onClick={handleCopyLink}
                className={`w-full px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  copied
                    ? 'bg-green-100 text-green-800 border border-green-200'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copiado al portapapeles
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copiar al portapapeles
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500">
                Validez: 1 hora. Compartir solo con el usuario.
              </p>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetLinkModal;
